/**
 * ScrollToTopButton - Floating scroll-to-top button
 * Elvis Aloha Collection
 * 
 * Appears after ~400-500px of scrolling
 * Supports keyboard activation and visible focus
 * Respects reduced-motion preferences
 * Positioned to avoid overlapping map navigator
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';

const ScrollToTopButton = ({ 
  targetRef = null,  // If provided, scrolls this element instead of window
  threshold = 400,   // Show button after this many pixels of scroll
  bottomOffset = 24, // Default bottom offset
  rightOffset = 24,  // Default right offset
  extraBottomOffset = 0, // Extra offset for avoiding navigator, etc.
  zIndex = 1000
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const buttonRef = useRef(null);
  
  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  // Handle scroll visibility
  useEffect(() => {
    const target = targetRef?.current || window;
    
    const handleScroll = () => {
      const scrollTop = targetRef?.current 
        ? targetRef.current.scrollTop 
        : window.pageYOffset || document.documentElement.scrollTop;
      
      setIsVisible(scrollTop > threshold);
    };
    
    target.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state
    
    return () => target.removeEventListener('scroll', handleScroll);
  }, [targetRef, threshold]);
  
  // Scroll to top handler
  const scrollToTop = useCallback(() => {
    const target = targetRef?.current || window;
    const behavior = prefersReducedMotion ? 'auto' : 'smooth';
    
    if (targetRef?.current) {
      targetRef.current.scrollTo({ top: 0, behavior });
    } else {
      window.scrollTo({ top: 0, behavior });
    }
  }, [targetRef, prefersReducedMotion]);
  
  // Handle keyboard activation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      scrollToTop();
    }
  }, [scrollToTop]);
  
  if (!isVisible) return null;
  
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label="Scroll to top"
      title="Scroll to top"
      onClick={scrollToTop}
      onKeyDown={handleKeyDown}
      style={{
        position: 'fixed',
        right: `${rightOffset}px`,
        bottom: `${bottomOffset + extraBottomOffset}px`,
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        background: 'rgba(26, 54, 93, 0.9)',
        color: 'white',
        border: '2px solid rgba(255, 255, 255, 0.2)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
        fontWeight: 'bold',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex,
        transition: prefersReducedMotion ? 'none' : 'opacity 0.2s ease, transform 0.2s ease, background 0.2s ease',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(10px)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(26, 54, 93, 1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(26, 54, 93, 0.9)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      onFocus={(e) => {
        e.currentTarget.style.outline = '2px solid #C9A962';
        e.currentTarget.style.outlineOffset = '2px';
      }}
      onBlur={(e) => {
        e.currentTarget.style.outline = 'none';
      }}
      data-testid="scroll-to-top-btn"
    >
      <svg 
        width="20" 
        height="20" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2.5" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline points="18 15 12 9 6 15" />
      </svg>
    </button>
  );
};

export default ScrollToTopButton;
